import { workspaceButtonStyles } from '../common/workspaceSurface';
/* =====================================================================
 * SkillModal — 스킬 관리 모달
 *
 * 탭: "내 스킬" | "스킬 레퍼런스" | "스킬 가이드"
 *
 * 카드/그리드 styled 요소는 `skillCards.tsx` 에 있고, `MySkillsTab` /
 * `ReferenceTab` 은 마켓플레이스 페이지(`skillMarketplace/`)가 그대로
 * 재사용하므로 named export 다.
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import type { SkillSummary } from '@auw/api-client';
import SkillSourcePanel, { type SkillSourceTarget } from './SkillSourcePanel';
import { SkillPager, usePagedList } from './skillPagination';
import {
  Plus,
  Trash2,
  FileText,
  BookOpen,
  FolderOpen,
  Sparkles,
  AlertCircle,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  Code,
  BarChart3,
  Image,
  Megaphone,
  Search,
  Languages,
  Workflow,
  GraduationCap,
  Presentation,
  Star,
  Pencil,
  Info,
  Share2,
  X,
} from 'lucide-react';
import { Modal, AccountAvatar, Button, Tooltip, ConfirmDialog } from '@auw/workspace-ui';
import { type TabItem } from '@auw/workspace-ui';
import { Chip } from '@auw/workspace-ui';
import { useSkillStore, useUserStore, useChatStore } from '@auw/stores';
import { useReboModeStore } from '@auw/stores';
import { useOfficialSkillStore } from '@auw/stores';
import type { OfficialSkillSummary } from '@auw/api-client';
import { resolveSkillReferenceLogo, useTenant } from '@auw/tenant';

import { resolveLocalized } from '@auw/workspace-utils';
import {
  CardGrid,
  DeleteSkillBtn,
  DetailBtn,
  EditNameInput,
  EmptyState,
  ErrorBanner,
  FavBtn,
  RefAvatar,
  RefCardBody,
  RefCardFooter,
  RefCardHeader,
  RefCardNew,
  RefCatLabel,
  RefCreator,
  RefDesc,
  RefEmptyWrap,
  RefFooterActions,
  RefHeaderText,
  RefLoadingWrap,
  RefSearchClear,
  RefSearchInput,
  RefSearchWrap,
  RefSkillName,
  RefSpinner,
  ScrollArea,
  ShareBtn,
  SkeletonBody,
  SkeletonCard,
  SkeletonIconArea,
  SkeletonLine,
  SkillTabs,
  SkillTitleRow,
  TitleEditBtn,
  UploadCard,
  UploadHint,
  UploadIconWrap,
  UploadIconZone,
  UploadInfoZone,
  UploadLabel,
  isZipSource,
} from './skillCards';

/**
 * Default reference-card hero used when the active tenant doesn't
 * declare `assets.skills.referenceCardLogo`. Resolved through
 * `resolveSkillReferenceLogo()` so a tenant override automatically
 * takes precedence on every render.
 */
const DEFAULT_SKILL_REF_LOGO = '/favicon-128.png';

type RefCategory = {
  id: string;
  labelKey: string;
  icon: React.ReactNode;
  skill: { id: string; nameKey: string; descKey: string; tags: string[] };
};

const REF_CATEGORIES: RefCategory[] = [
  { id: 'all', labelKey: 'skill.refCat.all', icon: <Sparkles size={14} />,
    skill: { id: '', nameKey: '', descKey: '', tags: [] } },
  { id: 'document', labelKey: 'skill.refCat.document', icon: <FileText size={14} />,
    skill: { id: 'ref-doc', nameKey: 'skill.refSkill.docName', descKey: 'skill.refSkill.docDesc', tags: ['skill.refTag.document', 'skill.refTag.report'] } },
  { id: 'presentation', labelKey: 'skill.refCat.presentation', icon: <Presentation size={14} />,
    skill: { id: 'ref-ppt', nameKey: 'skill.refSkill.pptName', descKey: 'skill.refSkill.pptDesc', tags: ['skill.refTag.ppt', 'skill.refTag.design'] } },
  { id: 'code', labelKey: 'skill.refCat.code', icon: <Code size={14} />,
    skill: { id: 'ref-code', nameKey: 'skill.refSkill.codeName', descKey: 'skill.refSkill.codeDesc', tags: ['skill.refTag.dev', 'skill.refTag.code'] } },
  { id: 'data', labelKey: 'skill.refCat.data', icon: <BarChart3 size={14} />,
    skill: { id: 'ref-data', nameKey: 'skill.refSkill.dataName', descKey: 'skill.refSkill.dataDesc', tags: ['skill.refTag.data', 'skill.refTag.analysis'] } },
  { id: 'image', labelKey: 'skill.refCat.image', icon: <Image size={14} />,
    skill: { id: 'ref-image', nameKey: 'skill.refSkill.imageName', descKey: 'skill.refSkill.imageDesc', tags: ['skill.refTag.image', 'skill.refTag.design'] } },
  { id: 'marketing', labelKey: 'skill.refCat.marketing', icon: <Megaphone size={14} />,
    skill: { id: 'ref-marketing', nameKey: 'skill.refSkill.marketingName', descKey: 'skill.refSkill.marketingDesc', tags: ['skill.refTag.marketing', 'skill.refTag.content'] } },
  { id: 'research', labelKey: 'skill.refCat.research', icon: <Search size={14} />,
    skill: { id: 'ref-research', nameKey: 'skill.refSkill.researchName', descKey: 'skill.refSkill.researchDesc', tags: ['skill.refTag.research', 'skill.refTag.analysis'] } },
  { id: 'translation', labelKey: 'skill.refCat.translation', icon: <Languages size={14} />,
    skill: { id: 'ref-trans', nameKey: 'skill.refSkill.transName', descKey: 'skill.refSkill.transDesc', tags: ['skill.refTag.translation', 'skill.refTag.multilang'] } },
  { id: 'automation', labelKey: 'skill.refCat.automation', icon: <Workflow size={14} />,
    skill: { id: 'ref-auto', nameKey: 'skill.refSkill.autoName', descKey: 'skill.refSkill.autoDesc', tags: ['skill.refTag.automation', 'skill.refTag.workflow'] } },
  { id: 'education', labelKey: 'skill.refCat.education', icon: <GraduationCap size={14} />,
    skill: { id: 'ref-edu', nameKey: 'skill.refSkill.eduName', descKey: 'skill.refSkill.eduDesc', tags: ['skill.refTag.education', 'skill.refTag.learning'] } },
];

interface SkillModalProps {
  open: boolean;
  onClose: () => void;
  initialTab?: 'my' | 'ref' | 'guide';
  centerPaddingLeft?: number;
  centerPaddingRight?: number;
}

const SkillModal: React.FC<SkillModalProps> = ({
  open,
  onClose,
  initialTab = 'my',
  centerPaddingLeft = 0,
  centerPaddingRight = 0,
}) => {
  const { t } = useTranslation('common');
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [sourceTarget, setSourceTarget] = useState<SkillSourceTarget | null>(null);
  const [sourceState, setSourceState] = useState({ dirty: false, saving: false });
  const canLeaveSource = () => !sourceState.saving && (!sourceState.dirty || window.confirm(t('skill.source.discard')));
  const closeSource = () => { if (canLeaveSource()) { setSourceTarget(null); setSourceState({ dirty: false, saving: false }); } };
  const closeModal = () => { if (canLeaveSource()) onClose(); };
  const {
    skills, activeSkillIds, isLoading, error,
    fetchSkills, uploadSkills, deleteSkill, toggleSkill,
  } = useSkillStore();

  const tabs: TabItem[] = [
    { id: 'my', label: t('skill.tabMy'), icon: <FolderOpen size={14} /> },
    { id: 'ref', label: t('skill.tabRef'), icon: <BookOpen size={14} /> },
    { id: 'guide', label: t('skill.tabGuide'), icon: <HelpCircle size={14} /> },
  ];

  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
      setSourceTarget(null);
      setSourceState({ dirty: false, saving: false });
      fetchSkills();
    }
  }, [open, initialTab, fetchSkills]);

  return (
    <Modal
      open={open}
      onClose={closeModal}
      title={t('skill.modalTitle')}
      width="1080px"
      bodyPadding="0"
      bodyOverflowY="hidden"
      centerPaddingLeft={centerPaddingLeft}
      centerPaddingRight={centerPaddingRight}
    >
      {!sourceTarget && <SkillTabs items={tabs} activeId={activeTab} onChange={setActiveTab} />}

      <TabContent>
        {sourceTarget ? <SkillSourcePanel key={`${sourceTarget.kind}:${sourceTarget.id}`} target={sourceTarget}
          onBack={closeSource} onStateChange={setSourceState} onSaved={() => { void fetchSkills(); }} /> : <>
        {activeTab === 'my' && (
          <MySkillsTab
            onOpenSource={setSourceTarget}
            skills={skills}
            activeSkillIds={activeSkillIds}
            isLoading={isLoading}
            error={error}
            onUpload={uploadSkills}
            onDelete={deleteSkill}
            onToggle={toggleSkill}
            onClose={onClose}
          />
        )}
        {activeTab === 'ref' && (
          <ReferenceTab
            onOpenSource={setSourceTarget}
            activeSkillIds={activeSkillIds}
            onToggle={toggleSkill}
            onClose={onClose}
          />
        )}
        {activeTab === 'guide' && <GuideTab />}
        </>}
      </TabContent>
    </Modal>
  );
};

export default SkillModal;

/* ------------------------------------------------------------------ */
/* My Skills Tab                                                       */
/* ------------------------------------------------------------------ */

export interface MySkillsTabProps {
  onOpenSource: (target: SkillSourceTarget) => void;
  skills: SkillSummary[];
  activeSkillIds: string[];
  isLoading: boolean;
  error: string | null;
  onUpload: (files: File[]) => Promise<unknown>;
  onDelete: (id: string) => Promise<void>;
  onToggle: (id: string) => void;
  onClose: () => void;
  /** 카드 푸터에 "공유하기 / 공유 취소" 버튼을 둔다 (마켓플레이스 페이지). */
  enableSharing?: boolean;
  /** 카드의 "적용하기" 버튼. 페이지는 끈다 — 적용은 채팅 입력창의 모달에서만. */
  showApply?: boolean;
  /** 한 페이지의 스킬 카드 수. 없으면 전부 그린다(모달). */
  pageSize?: number;
}

/** 공유 확인 대기 중인 카드. `unshare` 가 true 면 "공유 취소" 확인이다. */
interface PendingShare { skillId: string; unshare: boolean }

export const MySkillsTab: React.FC<MySkillsTabProps> = ({
  skills, activeSkillIds, isLoading, error,
  onUpload, onDelete, onToggle, onClose, onOpenSource,
  enableSharing = false, showApply = true, pageSize,
}) => {
  const { t } = useTranslation('common');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);
  const editCancelled = useRef(false);
  const [pendingShare, setPendingShare] = useState<PendingShare | null>(null);
  const { renameSkill } = useSkillStore();
  /* 작성자 표시는 사이드바 계정 블록과 같은 아바타(사진 → 이니셜)를 쓴다. */
  const user = useUserStore((s) => s.user);
  const userName = user?.name || user?.email || t('skill.user');

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const arr = Array.from(files);
      if (!arr.length || isLoading) return;
      await onUpload(arr);
    },
    [onUpload, isLoading],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const startEdit = useCallback((skill: SkillSummary) => {
    editCancelled.current = false;
    setEditingId(skill.skill_id);
    setEditName(skill.name);
    setTimeout(() => editInputRef.current?.focus(), 0);
  }, []);

  const commitEdit = useCallback(async () => {
    const cancelled = editCancelled.current;
    editCancelled.current = true;
    setEditingId(null);
    if (!cancelled && editingId && editName.trim()) {
      await renameSkill(editingId, editName.trim());
    }
  }, [editingId, editName, renameSkill]);

  const { setMode } = useReboModeStore();
  const handleApply = useCallback((skillId: string) => {
    const willActivate = !activeSkillIds.includes(skillId);
    onToggle(skillId);
    setMode(willActivate ? 'skill' : 'auto');
    if (willActivate) onClose();
  }, [activeSkillIds, onToggle, setMode, onClose]);

  const paged = usePagedList(skills, pageSize);

  return (
    <ScrollArea>
      {enableSharing && (
        <SkillShareDialog pending={pendingShare} onClose={() => setPendingShare(null)} />
      )}
      {error && (
        <ErrorBanner role="alert">
          <AlertCircle size={14} />
          {error}
        </ErrorBanner>
      )}

      <CardGrid>
        <UploadCard
          $over={isDragOver}
          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={onDrop}
          onClick={() => { if (!isLoading) fileInputRef.current?.click(); }}
        >
          <input
            ref={fileInputRef}
            type="file"
            disabled={isLoading}
            aria-label={t('skill.uploadLabel')}
            accept=".md,.zip"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files?.length) handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <UploadIconZone>
            <UploadIconWrap>
              <Plus size={28} strokeWidth={1.5} />
            </UploadIconWrap>
          </UploadIconZone>
          <UploadInfoZone>
            <UploadLabel>{t('skill.uploadLabel')}</UploadLabel>
            <UploadHint>{t('skill.uploadHint')}</UploadHint>
          </UploadInfoZone>
        </UploadCard>

        {isLoading && (
          <SkeletonCard>
            <SkeletonIconArea />
            <SkeletonBody>
              <SkeletonLine $width="70%" $height="13px" />
              <SkeletonLine $width="90%" $height="10px" />
              <SkeletonLine $width="40%" $height="10px" />
            </SkeletonBody>
          </SkeletonCard>
        )}

        {!isLoading && paged.pageItems.map((skill) => {
          const isActive = activeSkillIds.includes(skill.skill_id);
          const isZip = isZipSource(skill.source_filename);
          const isEditing = editingId === skill.skill_id;
          return (
            <RefCardNew key={skill.skill_id} $active={showApply && isActive}>
              <RefCardHeader>
                <AccountAvatar
                  size={36}
                  name={user?.name}
                  email={user?.email}
                  picture={user?.picture}
                />
                <RefHeaderText>
                  {isEditing ? (
                    <EditNameInput
                      ref={editInputRef}
                      aria-label={t('skill.rename')}
                      value={editName}
                      maxLength={255}
                      onChange={(e) => setEditName(e.target.value)}
                      onBlur={commitEdit}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                        if (e.key === 'Escape') { e.stopPropagation(); editCancelled.current = true; setEditingId(null); }
                      }}
                    />
                  ) : (
                    <SkillTitleRow>
                      <RefSkillName title={skill.name}>{skill.name}</RefSkillName>
                      <Tooltip label={t('skill.rename')}>
                        <TitleEditBtn type="button" onClick={() => startEdit(skill)} aria-label={t('skill.rename')}><Pencil size={13} /></TitleEditBtn>
                      </Tooltip>
                    </SkillTitleRow>
                  )}
                  <RefCreator title={userName}>{userName}</RefCreator>
                </RefHeaderText>
              </RefCardHeader>
              <RefCardBody>
                <RefDesc>{skill.body_preview || skill.description || t('skill.noDescription')}</RefDesc>
                <RefCatLabel>
                  {skill.is_shared && <Chip size="sm" variant="subtle" data-testid="skill-shared-chip">{t('skill.shared')}</Chip>}
                  {skill.has_references && <Chip size="sm" variant="subtle">references</Chip>}
                  <Chip size="sm" variant="subtle">{isZip ? '.zip' : '.md'}</Chip>
                </RefCatLabel>
              </RefCardBody>
              <RefCardFooter>
                {showApply && (
                  <Button
                    type="button"
                    size="sm"
                    variant="surface"
                    aria-pressed={isActive}
                    onClick={() => handleApply(skill.skill_id)}
                  >
                    {isActive ? t('skill.deactivate') : t('skill.apply')}
                  </Button>
                )}
                <RefFooterActions>
                  <Tooltip label={t('skill.edit')}>
                    <DetailBtn
                      type="button"
                      onClick={() => onOpenSource({ id: skill.skill_id, name: skill.name, kind: 'user' })}
                      aria-label={t('skill.edit')}
                    >
                      <Pencil size={16} />
                    </DetailBtn>
                  </Tooltip>
                  <Tooltip label={t('skill.delete')}>
                    <DeleteSkillBtn
                      type="button"
                      onClick={() => onDelete(skill.skill_id)}
                      aria-label={t('skill.delete')}
                    >
                      <Trash2 size={16} />
                    </DeleteSkillBtn>
                  </Tooltip>
                  {enableSharing && (
                    <Tooltip label={t(skill.is_shared ? 'skill.unshare' : 'skill.share')}>
                      <ShareBtn
                        type="button"
                        $active={!!skill.is_shared}
                        onClick={() => setPendingShare({ skillId: skill.skill_id, unshare: !!skill.is_shared })}
                        aria-label={t(skill.is_shared ? 'skill.unshare' : 'skill.share')}
                        aria-pressed={!!skill.is_shared}
                      >
                        <Share2 size={16} />
                      </ShareBtn>
                    </Tooltip>
                  )}
                </RefFooterActions>
              </RefCardFooter>
            </RefCardNew>
          );
        })}
      </CardGrid>

      {!isLoading && <SkillPager page={paged.page} pageCount={paged.pageCount} onChange={paged.setPage} />}

      {skills.length === 0 && !isLoading && (
        <EmptyState>{t('skill.empty')}</EmptyState>
      )}
    </ScrollArea>
  );
};

/* 공유 / 공유 취소 확인. 스토어 호출과 토스트까지 여기서 끝낸다 — 카드
 * 하나가 아니라 탭 하나에 대화상자 하나만 두기 위해 `MySkillsTab` 이
 * 대기 중인 카드만 넘긴다. `enableSharing` 일 때만 마운트되므로 모달
 * 안의 내 스킬 탭은 이 훅들을 건드리지 않는다. */
const SkillShareDialog: React.FC<{ pending: PendingShare | null; onClose: () => void }> = ({ pending, onClose }) => {
  const { t } = useTranslation('common');
  const { shareSkill, unshareSkill } = useSkillStore();
  const addToast = useChatStore((s) => s.addToast);
  const unshare = pending?.unshare ?? false;

  const handleConfirm = useCallback(async () => {
    if (!pending) return;
    const ok = pending.unshare ? await unshareSkill(pending.skillId) : await shareSkill(pending.skillId);
    if (ok) addToast({ type: 'success', title: t(pending.unshare ? 'skill.unshareSuccess' : 'skill.shareSuccess') });
    else addToast({ type: 'error', title: t(pending.unshare ? 'skill.unshareFail' : 'skill.shareFail') });
    onClose();
  }, [pending, shareSkill, unshareSkill, addToast, t, onClose]);

  return (
    <ConfirmDialog
      open={pending !== null}
      title={t(unshare ? 'skill.unshareConfirmTitle' : 'skill.shareConfirmTitle')}
      description={t(unshare ? 'skill.unshareConfirmBody' : 'skill.shareConfirmBody')}
      confirmLabel={t('button.confirm')}
      cancelLabel={t('button.cancel')}
      tone="default"
      onConfirm={handleConfirm}
      onClose={onClose}
    />
  );
};

/* ------------------------------------------------------------------ */
/* Reference Tab (Mockup)                                              */
/* ------------------------------------------------------------------ */

export interface ReferenceTabProps {
  onOpenSource: (target: SkillSourceTarget) => void;
  activeSkillIds: string[];
  onToggle: (id: string) => void;
  onClose: () => void;
  /** `MySkillsTabProps.showApply` 와 같은 계약. */
  showApply?: boolean;
  /** `MySkillsTabProps.pageSize` 와 같은 계약. */
  pageSize?: number;
}

const OUTPUT_FORMAT_LABELS: Record<string, string> = {
  auto: 'Auto',
  text: 'Text',
  image: 'Image',
  video: 'Video',
  docx: 'DOCX',
  pptx: 'PPTX',
  hwpx: 'HWPX',
  xlsx: 'XLSX',
  interactive_ui: 'Interactive UI',
};

export const ReferenceTab: React.FC<ReferenceTabProps> = ({ activeSkillIds, onToggle, onClose, onOpenSource, showApply = true, pageSize }) => {
  const { t, i18n } = useTranslation('common');
  const lang = i18n.language;
  const tenant = useTenant();
  /* Phase 12 — tenant assets:
   * Reference cards always render an image; missing tenant overrides
   * fall through to `/favicon-128.png` so B2C and any tenant that
   * leaves `assets.skills.referenceCardLogo` unset keeps the legacy
   * "Rebo" hero. */
  const skillRefLogo = useMemo(
    () => resolveSkillReferenceLogo(tenant, DEFAULT_SKILL_REF_LOGO),
    [tenant],
  );
  const [activeCat, setActiveCat] = useState('all');
  const chipBarRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const { favoriteRefIds, fetchFavorites, toggleFavorite } = useSkillStore();
  const { skills: officialSkills, isLoading: officialLoading, fetchPublicSkills } = useOfficialSkillStore();
  const { setMode } = useReboModeStore();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchFavorites();
    fetchPublicSkills();
  }, [fetchFavorites, fetchPublicSkills]);

  const updateScrollFlags = useCallback(() => {
    const el = chipBarRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScrollFlags();
    const el = chipBarRef.current;
    if (el) el.addEventListener('scroll', updateScrollFlags, { passive: true });
    return () => el?.removeEventListener('scroll', updateScrollFlags);
  }, [updateScrollFlags]);

  const scrollChips = useCallback((dir: 'left' | 'right') => {
    chipBarRef.current?.scrollBy({ left: dir === 'right' ? 180 : -180, behavior: 'smooth' });
  }, []);

  const visibleSkills = useMemo(() => {
    let list: OfficialSkillSummary[] = officialSkills;
    if (activeCat !== 'all') {
      list = list.filter((s) => s.category === activeCat);
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((s) => {
        const localName = resolveLocalized(s.display_name, lang, s.name);
        const localDesc = resolveLocalized(s.display_description, lang, s.description);
        return (
          localName.toLowerCase().includes(q) ||
          localDesc.toLowerCase().includes(q) ||
          s.name.toLowerCase().includes(q)
        );
      });
    }
    return [...list].sort((a, b) => {
      const aFav = favoriteRefIds.includes(a.skill_id) ? 0 : 1;
      const bFav = favoriteRefIds.includes(b.skill_id) ? 0 : 1;
      return aFav - bFav;
    });
  }, [officialSkills, activeCat, favoriteRefIds, searchQuery, lang]);

  const catLookup = useMemo(() => {
    const map: Record<string, RefCategory> = {};
    for (const c of REF_CATEGORIES) map[c.id] = c;
    return map;
  }, []);

  const handleRefApply = useCallback((skillId: string) => {
    const willActivate = !activeSkillIds.includes(skillId);
    onToggle(skillId);
    setMode(willActivate ? 'skill' : 'auto');
    if (willActivate) onClose();
  }, [activeSkillIds, onToggle, setMode, onClose]);

  /* 카테고리·검색이 바뀌면 1페이지로. */
  const paged = usePagedList(visibleSkills, pageSize, `${activeCat}|${searchQuery}`);

  return (
    <RefTabWrap>
      <CatBarWrap>
        {canScrollLeft && (
          <CatFade $side="left" />
        )}
        {canScrollLeft && (
          <CatArrow $side="left" onClick={() => scrollChips('left')}>
            <ChevronLeft size={16} />
          </CatArrow>
        )}
        <CatChipBar ref={chipBarRef}>
          {REF_CATEGORIES.map((cat) => (
            <CatChip
              key={cat.id}
              $active={activeCat === cat.id}
              onClick={() => setActiveCat(cat.id)}
            >
              {cat.icon}
              <span>{t(cat.labelKey)}</span>
            </CatChip>
          ))}
        </CatChipBar>
        {canScrollRight && (
          <CatFade $side="right" />
        )}
        {canScrollRight && (
          <CatArrow $side="right" onClick={() => scrollChips('right')}>
            <ChevronRight size={16} />
          </CatArrow>
        )}
      </CatBarWrap>

      <RefSearchWrap>
        <Search size={14} />
        <RefSearchInput
          type="text"
          placeholder={t('skillCreate.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <RefSearchClear
            type="button"
            onClick={() => setSearchQuery('')}
            aria-label={t('translation.clear', { defaultValue: '입력 지우기' })}
          >
            <X size={12} />
          </RefSearchClear>
        )}
      </RefSearchWrap>

      <ScrollArea>
        {officialLoading ? (
          <RefLoadingWrap>
            <RefSpinner size={18} />
          </RefLoadingWrap>
        ) : visibleSkills.length === 0 ? (
          <RefEmptyWrap>
            <Sparkles size={24} />
            <span>{t('skill.refEmpty')}</span>
          </RefEmptyWrap>
        ) : (
          <CardGrid>
            {paged.pageItems.map((skill) => {
              const isFav = favoriteRefIds.includes(skill.skill_id);
              const isActive = activeSkillIds.includes(skill.skill_id);
              const cat = catLookup[skill.category];
              return (
                <RefCardNew key={skill.skill_id} $active={showApply && isActive}>
                  <RefCardHeader>
                    <RefAvatar src={skillRefLogo.src} alt={skillRefLogo.alt ?? 'Rebo'} />
                    <RefHeaderText>
                      <RefSkillName>{resolveLocalized(skill.display_name, lang, skill.name)}</RefSkillName>
                      <RefCreator>{skill.creator_name || skill.creator_id || 'askyour.ai'}</RefCreator>
                    </RefHeaderText>
                  </RefCardHeader>

                  <RefCardBody>
                    <RefDesc>{resolveLocalized(skill.display_description, lang, skill.description)}</RefDesc>
                    <RefCatLabel>
                      {cat && <Chip size="sm" variant="subtle">{t(cat.labelKey)}</Chip>}
                    </RefCatLabel>
                  </RefCardBody>

                  <RefCardFooter>
                    {showApply && (
                      <Button
                        type="button"
                        size="sm"
                        variant="surface"
                        aria-pressed={isActive}
                        onClick={(e) => { e.stopPropagation(); handleRefApply(skill.skill_id); }}
                      >
                        {isActive ? t('skill.deactivate') : t('skill.apply')}
                      </Button>
                    )}
                    <RefFooterActions>
                      <Tooltip label={t('skill.detail', 'Detail')}>
                        <DetailBtn
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onOpenSource({ id: skill.skill_id, name: resolveLocalized(skill.display_name, lang, skill.name), kind: 'official' }); }}
                          aria-label={t('skill.detail', 'Detail')}
                        >
                          <Info size={16} />
                        </DetailBtn>
                      </Tooltip>
                      <Tooltip label={t('skill.favorite')}>
                        <FavBtn
                          type="button"
                          $active={isFav}
                          onClick={(e) => { e.stopPropagation(); toggleFavorite(skill.skill_id); }}
                          aria-label={t('skill.favorite')}
                          aria-pressed={isFav}
                        >
                          <Star size={16} fill={isFav ? 'currentColor' : 'none'} />
                        </FavBtn>
                      </Tooltip>
                    </RefFooterActions>
                  </RefCardFooter>
                </RefCardNew>
              );
            })}
          </CardGrid>
        )}
        {!officialLoading && <SkillPager page={paged.page} pageCount={paged.pageCount} onChange={paged.setPage} />}
      </ScrollArea>
    </RefTabWrap>
  );
};

/* ------------------------------------------------------------------ */
/* Guide Tab                                                           */
/* ------------------------------------------------------------------ */

const GuideTab: React.FC = () => {
  const { t } = useTranslation('common');
  return (
    <ScrollArea>
      <GuideContent>
        <GuideSection>
          <GuideH2>{t('skill.guide.overviewTitle')}</GuideH2>
          <GuideP>{t('skill.guide.overviewBody')}</GuideP>
        </GuideSection>

        <GuideSection>
          <GuideH2>{t('skill.guide.structureTitle')}</GuideH2>
          <GuideP>{t('skill.guide.structureDesc')}</GuideP>
          <GuideCode>{`my-skill/
├── SKILL.md            # ${t('skill.guide.structureSkillMd')}
└── references/         # ${t('skill.guide.structureRefs')}`}</GuideCode>
        </GuideSection>

        <GuideSection>
          <GuideH2>{t('skill.guide.frontmatterTitle')}</GuideH2>
          <GuideP>{t('skill.guide.frontmatterWhat')}</GuideP>
          <GuideCode>{`---
name: my-skill
description: ${t('skill.guide.formatExampleDesc')}
metadata:
  author: your-name
  version: "1.0"
---

${t('skill.guide.formatExampleBody')}`}</GuideCode>
          <GuideP>{t('skill.guide.frontmatterExplain')}</GuideP>
          <GuideCallout>{t('skill.guide.frontmatterOptional')}</GuideCallout>
        </GuideSection>

        <GuideSection>
          <GuideH2>{t('skill.guide.fieldsTitle')}</GuideH2>
          <GuideTable>
            <thead>
              <tr>
                <GuideTh>{t('skill.guide.fieldCol')}</GuideTh>
                <GuideTh>{t('skill.guide.requiredCol')}</GuideTh>
                <GuideTh>{t('skill.guide.descCol')}</GuideTh>
              </tr>
            </thead>
            <tbody>
              <tr>
                <GuideTd><GuideInlineCode>name</GuideInlineCode></GuideTd>
                <GuideTd>{t('skill.guide.optional')}</GuideTd>
                <GuideTd>{t('skill.guide.fieldName')}</GuideTd>
              </tr>
              <tr>
                <GuideTd><GuideInlineCode>description</GuideInlineCode></GuideTd>
                <GuideTd>{t('skill.guide.optional')}</GuideTd>
                <GuideTd>{t('skill.guide.fieldDescription')}</GuideTd>
              </tr>
              <tr>
                <GuideTd><GuideInlineCode>metadata</GuideInlineCode></GuideTd>
                <GuideTd>{t('skill.guide.optional')}</GuideTd>
                <GuideTd>{t('skill.guide.fieldMetadata')}</GuideTd>
              </tr>
            </tbody>
          </GuideTable>
        </GuideSection>

        <GuideSection>
          <GuideH2>{t('skill.guide.foldersTitle')}</GuideH2>

          <GuideH3>{t('skill.guide.refsFolderTitle')}</GuideH3>
          <GuideP>{t('skill.guide.refsFolderDesc')}</GuideP>
          <GuideP>{t('skill.guide.refsFolderExts')}</GuideP>

          <GuideCallout>{t('skill.guide.blockedFiles')}</GuideCallout>
        </GuideSection>

        <GuideSection>
          <GuideH2>{t('skill.guide.uploadTitle')}</GuideH2>
          <GuideOl>
            <li>{t('skill.guide.uploadStep1')}</li>
            <li>{t('skill.guide.uploadStep2')}</li>
            <li>{t('skill.guide.uploadStep3')}</li>
          </GuideOl>
        </GuideSection>

        <GuideSection>
          <GuideH2>{t('skill.guide.activateTitle')}</GuideH2>
          <GuideP>{t('skill.guide.activateBody')}</GuideP>
        </GuideSection>

        <GuideSection>
          <GuideH2>{t('skill.guide.zipTitle')}</GuideH2>
          <GuideP>{t('skill.guide.zipDesc')}</GuideP>
          <GuideCode>{`my-skill.zip/
├── instructions.md     # ${t('skill.guide.zipRootMd')}
└── references/
    ├── guide.md
    ├── report.pdf
    ├── template.docx
    ├── data.xlsx
    ├── slides.pptx
    └── example.py`}</GuideCode>
          <GuideCallout>{t('skill.guide.zipNote')}</GuideCallout>
        </GuideSection>
      </GuideContent>
    </ScrollArea>
  );
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const TabContent = styled.div`
  flex: 1;
  height: min(680px, 75vh);
  max-height: min(680px, 75vh);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 600px) {
    height: 380px;
    max-height: 380px;
  }
`;

/* ---- Ref Tab Layout ---- */

const RefTabWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

/* ---- Category Chip Bar ---- */

const CatBarWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 16px 28px 12px;
  flex-shrink: 0;
`;

const CatChipBar = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 2px 4px;
  flex: 1;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CatChip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  flex-shrink: 0;
  transition: all ${({ theme }) => theme.transitions.fast};
  /* 활성 톤은 공통 Chip 의 active 와 같은 accent 계열 — 모달 밖의 필터 칩과
   * 같은 언어를 쓴다. */
  border: 1px solid ${({ theme, $active }) =>
    $active ? theme.colors.brand : theme.colors.border};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.brandSoft : 'transparent'};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.textPrimary : theme.colors.textSecondary};

  &:hover {
    border-color: ${({ theme }) => theme.colors.brand};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const CatFade = styled.div<{ $side: 'left' | 'right' }>`
  position: absolute;
  ${({ $side }) => ($side === 'left' ? 'left: 28px;' : 'right: 28px;')}
  top: 0;
  bottom: 0;
  width: 40px;
  z-index: 1;
  pointer-events: none;
  /* 모달 표면(bgPrimary)에서 투명으로 — 표면색이 테마를 따라가므로 여기도 토큰. */
  background: ${({ $side, theme }) =>
    $side === 'left'
      ? `linear-gradient(to right, ${theme.colors.bgPrimary} 0%, transparent 100%)`
      : `linear-gradient(to left, ${theme.colors.bgPrimary} 0%, transparent 100%)`};
`;

const CatArrow = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  ${({ $side }) => ($side === 'left' ? 'left: 28px;' : 'right: 28px;')}
  z-index: 2;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.08)'};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.brand};
    border-color: ${({ theme }) => theme.colors.brand};
  }
  ${workspaceButtonStyles}
`;

/* ---- Guide Tab Styles ---- */

const GuideContent = styled.div`
  max-width: 720px;
  margin: 0 auto;
`;

const GuideSection = styled.section`
  margin-bottom: 28px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const GuideH2 = styled.h2`
  font-size: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 10px;
  line-height: 1.4;
`;

const GuideH3 = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 16px 0 6px;
  line-height: 1.4;

  &:first-child {
    margin-top: 0;
  }
`;

const GuideP = styled.p`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;
  margin: 0 0 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const GuideCode = styled.pre`
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 0.75rem;
  line-height: 1.65;
  padding: 14px 16px;
  border-radius: 10px;
  margin: 8px 0 12px;
  overflow-x: auto;
  white-space: pre;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const GuideInlineCode = styled.code`
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 0.75rem;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const GuideTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
  margin: 8px 0 12px;
`;

const GuideTh = styled.th`
  text-align: left;
  padding: 8px 12px;
  font-weight: 600;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const GuideTd = styled.td`
  padding: 8px 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  border-bottom: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'};
  font-size: 0.8125rem;
  line-height: 1.5;
`;

const GuideOl = styled.ol`
  margin: 4px 0 0;
  padding-left: 20px;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.8;
`;

const GuideCallout = styled.div`
  margin-top: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.75rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)'};
  border-left: 3px solid ${({ theme }) => theme.colors.brand};
`;
